import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {onOpenCropModal, onOpenMaskingModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  removeImageBackgroundFromId,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {openPosterEditorItemFlipModal} from '@Modals/poster-editor-item-flip-modal';
import {openPosterEditorItemStretchModal} from '@Modals/poster-editor-item-stretch-modal';
import {openPosterEditorItemAdjustmentsModal} from '@Modals/poster-editor-item-adjustments-modal';
import {openPosterEditorItemFiltersModal} from '@Modals/poster-editor-item-filters-modal';
import {openPosterEditorItemEffectsModal} from '@Modals/poster-editor-item-effects-modal';
import {openPosterEditorDrawingColorModal} from '@Modals/poster-editor-drawing-color-modal';
import {openReplaceMediaModal} from '@Modals/replace-media-modal';
import {useEditorPrefetchMediaMobileModules} from '@Hooks/poster-editor/useEditorPrefetchMediaMobileModules';
import {RemoveImageBackgroundNotificationTag} from '@Components/poster-editor/components/remove-image-background-notification-tag';
import {ImageItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {openPosterEditorRoundnessEffectModal} from '@Modals/poster-editor-roundness-effect-modal/poster-editor-roundness-effect-modal';
import styles from './image-bottom-bar.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

export function ImageBottomBar(): ReactElement | null {
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isLocked = useGetKeyFromActiveItem('lockMovementX');
  const isBackgroundBeingRemoved = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId ?? ''];
    if (item && 'removeBackground' in item) {
      return item.removeBackground.isBackgroundBeingRemoved;
    }
    return false;
  });
  const isBackgroundRemoved = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId ?? ''];
    if (item && 'removeBackground' in item) {
      return item.removeBackground.isBackgroundRemoved;
    }
    return false;
  });
  useEditorPrefetchMediaMobileModules();

  if (!activeItem?.isImage()) {
    return null;
  }

  const pulsatingDotForCopyStyles = (): ReactElement | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (itemId: ImageItemBottomBarOptions): void => {
    switch (itemId) {
      case ImageItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case ImageItemBottomBarOptions.DRAW_COLOR:
        openPosterEditorDrawingColorModal();
        break;
      case ImageItemBottomBarOptions.CROP:
        onOpenCropModal(activeItem);
        break;
      case ImageItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case ImageItemBottomBarOptions.MASK:
        void onOpenMaskingModal(activeItem);
        break;
      case ImageItemBottomBarOptions.REPLACE:
        openReplaceMediaModal();
        return;
      case ImageItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case ImageItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case ImageItemBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case ImageItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case ImageItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case ImageItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem?.uid);
        break;
      case ImageItemBottomBarOptions.FLIP:
        openPosterEditorItemFlipModal();
        break;
      case ImageItemBottomBarOptions.STRETCH:
        openPosterEditorItemStretchModal();
        break;
      case ImageItemBottomBarOptions.ADJUSTMENTS:
        openPosterEditorItemAdjustmentsModal();
        break;
      case ImageItemBottomBarOptions.FILTERS:
        openPosterEditorItemFiltersModal();
        break;
      case ImageItemBottomBarOptions.EFFECTS:
        openPosterEditorItemEffectsModal();
        break;
      case ImageItemBottomBarOptions.REMOVE_BG:
        onRemoveBgCallback();
        break;
      case ImageItemBottomBarOptions.ROUNDNESS:
        openPosterEditorRoundnessEffectModal();
        break;
      default:
    }
  };

  const onRemoveBgCallback = (): void => {
    removeImageBackgroundFromId(activeItem.uid);
    trackPosterBuilderGA4Events(GA4EventName.REMOVE_BACKGROUND, {[GA4EventParamName.SOURCE]: GA4EventSources.MOBILE_BOTTOM_BAR});
  };

  return (
    <BottomBar
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: ImageItemBottomBarOptions.DRAW_COLOR,
          icon: 'icon-colorpicker',
          text: window.i18next.t('pmwjs_color'),
          isHidden: (activeItem.isImage() && !activeItem.isDrawing()) || activeItem.isVideo(),
        },
        {
          id: ImageItemBottomBarOptions.CROP,
          icon: 'icon-crop',
          text: window.i18next.t('pmwjs_crop'),
          isHidden: activeItem.isDrawing() || activeItem.isIcon(),
        },
        {
          id: ImageItemBottomBarOptions.REMOVE_BG,
          icon: 'icon-remove-bg',
          text: window.i18next.t('pmwjs_ai_bg_remover'),
          tag: <RemoveImageBackgroundNotificationTag hashedFilename={activeItem.hashedFilename} imageSource={activeItem.imageSource} />,
          isLoading: isBackgroundBeingRemoved,
          isSelected: isBackgroundRemoved && !isBackgroundBeingRemoved,
          isTextBold: true,
          loadingText: window.i18next.t('pmwjs_removing_background'),
          isHidden: activeItem.isDrawing() || activeItem.isIcon(),
          textClasses: `spacing-p-0`,
          isDisabled: activeItem.isImageUploading(),
        },
        {
          id: ImageItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: ImageItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: ImageItemBottomBarOptions.MASK,
          icon: 'icon-mask',
          text: window.i18next.t('pmwjs_mask'),
          isHidden: activeItem.isDrawing() || activeItem.isIcon(),
        },
        {
          id: ImageItemBottomBarOptions.FILTERS,
          icon: 'icon-filters',
          text: window.i18next.t('pmwjs_filters'),
        },
        {
          id: ImageItemBottomBarOptions.EFFECTS,
          icon: 'icon-effects',
          text: window.i18next.t('pmwjs_effects'),
        },
        {
          id: ImageItemBottomBarOptions.ADJUSTMENTS,
          icon: 'icon-adjustments',
          text: window.i18next.t('pmwjs_adjustments'),
        },
        {
          id: ImageItemBottomBarOptions.ROUNDNESS,
          icon: 'icon-corner-top-left',
          text: window.i18next.t('pmwjs_roundness'),
          isHidden: activeItem.isDrawing() || activeItem.isIcon(),
        },
        {
          id: ImageItemBottomBarOptions.REPLACE,
          icon: 'icon-replace',
          text: window.i18next.t('pmwjs_replace'),
          isHidden: activeItem.isDrawing() || activeItem.isIcon(),
        },
        {
          id: ImageItemBottomBarOptions.FLIP,
          icon: 'icon-flip-horizontal',
          text: window.i18next.t('pmwjs_flip'),
        },
        {
          id: ImageItemBottomBarOptions.STRETCH,
          icon: 'icon-fit-to-width',
          text: window.i18next.t('pmwjs_stretch'),
        },
        {
          id: ImageItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: ImageItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: ImageItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: ImageItemBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: ImageItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: ImageItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
